import { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../AuthContext';
import { generateMagicFirstDraftWithOpenAi } from '../../utilityFunctions/openAiUtilities';
import { sendEmailWithResult } from '../../utilityFunctions/emailUtilities';

const COVER_LETTER_INSTRUCTIONS = 'Write a compelling one-page cover letter that introduces our organization, highlights our key achievements, and explains why we are uniquely qualified for this funding opportunity. Focus on connecting our mission with the funder\'s priorities while maintaining a professional and engaging tone.';

const MagicFirstDraftModal = ({ 
    setShowMagicFirstDraftModal,
    currentProgram,
    setShowLoadingModal,
    currentClientOrganization,
    organizationPrograms,
    funderNameForMagicDraft,
    funderDescriptionForMagicDraft,
    }) => {    

    const { authUser } = useContext(AuthContext);

    // New state variables for the form fields
    const [selectedProgramForDraft, setSelectedProgramForDraft] = useState();
    const [funderName, setFunderName] = useState(funderNameForMagicDraft || '');
    const [funderDescription, setFunderDescription] = useState(funderDescriptionForMagicDraft || '');   
    const [documentInstructions, setDocumentInstructions] = useState(COVER_LETTER_INSTRUCTIONS);    

    useEffect(() => {
        if (organizationPrograms && organizationPrograms.length > 0) {
            setSelectedProgramForDraft(organizationPrograms[0]);            
        }
    }, [organizationPrograms]);

    useEffect(() => {
        console.log("Selected Program for Draft:", selectedProgramForDraft);
    }, [selectedProgramForDraft]);
    
    const handleGenerateClicked = async (e) => {
        e.preventDefault();

        console.log("Current Program:", currentProgram);
        console.log("Document Instructions:", documentInstructions);
        console.log("Funder Description:", funderDescription);

        try {
            setShowLoadingModal(true);

            // export const generateMagicFirstDraftWithOpenAi = async  => {        
            const outputText = await generateMagicFirstDraftWithOpenAi(currentClientOrganization.name, selectedProgramForDraft.programName, selectedProgramForDraft.primaryState, selectedProgramForDraft.additionalDetails, funderName, funderDescription, documentInstructions );
            const cleanedResult = outputText.replace(/【.*?】/g, ''); // Remove any file annotations enclosed in "【】" from the result
            const formattedResult = cleanedResult.replaceAll('\n', '<br/>');
            
            // Send email with the result if user is authenticated
            if (authUser && authUser.email) {                                
                try {
                    await sendEmailWithResult(authUser.email, formattedResult);
                    alert(`Email sent successfully to: ${authUser.email}\n\nPlease check your spam folder if you don't see it immediately.`);
                } catch (emailError) {
                    console.error("Error sending email:", emailError);
                    alert(`Error sending email: ${emailError.message}`);
                }
            }     
            setShowLoadingModal(false);
        } catch (error) {
            console.error("Error generating first draft:", error);
            setShowLoadingModal(false);
        }
        
        setShowMagicFirstDraftModal(false);        
    };

    const handleCoverLetterClicked = (e) => {
        e.preventDefault();
        setDocumentInstructions(COVER_LETTER_INSTRUCTIONS);
    };

    const handleLetterOfIntentClicked = (e) => {
        e.preventDefault();
        setDocumentInstructions('Write a clear letter of intent that states our interest in the funding opportunity, outlines our proposed program\'s objectives and impact, and demonstrates our organization\'s track record of success. Include preliminary budget considerations and explain how our work aligns with the funder\'s strategic goals.');
    };

    return (
        <div 
            className="modal-overlay" 
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}
            onClick={() => setShowMagicFirstDraftModal(false)}
        >
            <div 
                className="modal mt-16 sm:mt-0 p-4 sm:p-6 lg:p-8 overflow-auto max-h-full mx-4 sm:mx-0 w-full sm:w-auto" 
                style={{ 
                    cursor: 'default', 
                    width: '95vw',
                    maxWidth: '500px',
                    minHeight: 'auto',
                    maxHeight: '90vh',
                    backgroundColor: 'white', 
                    borderRadius: '10px'
                }}
                onClick={(e) => e.stopPropagation()} // Prevent click event from propagating to the overlay
            >
                <div className="text-xl sm:text-2xl font-bold py-2 sm:py-4">✨ Generate Magic First Draft for {currentClientOrganization?.name}</div>
                <form onSubmit={handleGenerateClicked} className="flex flex-col gap-2 text-left">
                    {/* Program Selection */}
                    <div className="mt-2 sm:mt-4 flex flex-col gap-2">
                        <label className="mb-1 font-semibold text-xs sm:text-sm text-gray-500">Select Program</label>
                        {organizationPrograms && organizationPrograms.length > 0 ? (
                            <select 
                                value={selectedProgramForDraft?.id || ''} 
                                onChange={(e) => setSelectedProgramForDraft(organizationPrograms.find(p => p.id === e.target.value))} 
                                className="mb-1 p-2 border border-gray-300 rounded text-sm"
                            >
                                {organizationPrograms.map((program) => (
                                    <option key={program.id} value={program.id}>{program.programName}</option>
                                ))}
                            </select>
                        ) : (
                            <p className="text-xs sm:text-sm text-gray-500 italic">No programs available. Please create a program first.</p>
                        )}
                    </div>

                    {/* Funder Name */}
                    <div className="mt-2 sm:mt-4 flex flex-col gap-2">
                        <label className="font-semibold text-xs sm:text-sm text-gray-500">Funder Name</label>
                        <input 
                            placeholder="Funder Name" 
                            value={funderName} 
                            onChange={(e) => setFunderName(e.target.value)} 
                            className="mb-1 p-2 border border-gray-300 rounded text-sm"
                        />
                    </div>

                    {/* Funder Description */}
                    <div className="mt-2 sm:mt-4 flex flex-col gap-2">
                        <label className="font-semibold text-xs sm:text-sm text-gray-500">Funder Description</label>                        
                        <textarea 
                            placeholder="Funder Description" 
                            value={funderDescription} 
                            onChange={(e) => setFunderDescription(e.target.value)} 
                            className="mb-1 p-2 border border-gray-300 rounded text-sm"
                        />
                    </div>       
                    {/* Document Instructions */}
                    <div className="mt-2 sm:mt-4 flex flex-col gap-2">
                        <label className="mb-1 font-semibold text-xs sm:text-sm text-gray-500">Document Instructions</label>
                        <textarea 
                            placeholder="Create a cover letter " 
                            value={documentInstructions} 
                            onChange={(e) => setDocumentInstructions(e.target.value)} 
                            rows={4}
                            className="mb-1 p-2 border border-gray-300 rounded text-sm"
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <button
                            className="border border-indigo-400 text-indigo-400 rounded w-full sm:w-1/4 py-2 text-sm"
                            onClick={handleCoverLetterClicked}
                        >
                            + Cover Letter
                        </button>
                        <button
                            className="border border-indigo-400 text-indigo-400 rounded w-full sm:w-1/4 py-2 text-sm"
                            onClick={handleLetterOfIntentClicked}
                        >
                            + Letter of Intent
                        </button>
                    </div>

                    <button 
                        type="submit" 
                        onClick={handleGenerateClicked} 
                        className="p-2 sm:p-3 bg-indigo-700 hover:bg-indigo-900 text-white rounded mt-2 text-sm sm:text-base"
                    >
                        Generate
                    </button>
                </form>
            </div>
        </div>
    );
};  

export default MagicFirstDraftModal;